import { useEffect, useRef } from "react";
import Lenis from "lenis";
import { createLenis } from "../../lenis";
import gsap from "gsap";
import ScrollTrigger from "gsap/ScrollTrigger";
import SplitText from "gsap/SplitText";
import type { JoinLayoutProps } from "../types";
import { WeddingProgram } from "../WeddingProgram";
import { DEMO_IMAGES, DEMO_VIDEOS } from "../mock-data";

gsap.registerPlugin(ScrollTrigger, SplitText);

/**
 * SONATA — Vinyl Record / Album Cover
 *
 * Deep burgundy + cream + brass gold. A wedding rendered as a record
 * album. 6 acts = 6 tracks. Rotating vinyl. Audio waveforms.
 *
 *  ACT I  COVER — Album cover layout: hero label + tracklist on
 *         left, rotating vinyl on right (continuous spin).
 *         Marquee with track metadata top/bot.
 *  ACT II PINNED VINYL + TONE-ARM — Vinyl pinned center, tone-arm
 *         swings to different "tracks" as you scroll. Track label
 *         updates: 01 PRELUDE → 02 ENCOUNTER → 03 STORY → 04 VENUE
 *         → 05 PROGRAMME → 06 ENCORE. Each track shows different
 *         album-cover image swap behind. Waveform animates per track.
 *  ACT III AUDIO WAVEFORM — Full-screen SVG waveform that scroll
 *         scrubs through. Each peak corresponds to a wedding moment.
 *  ACT IV SIDE A / SIDE B GALLERY — Two columns labeled SIDE A and
 *         SIDE B, each with 4 "tracks" (photos as album covers).
 *  ACT V  DATE AS RUNTIME — Date displayed as a song length / album
 *         metadata table (RUNTIME 03:42, RELEASE DATE, MASTERED AT).
 *  ACT VI ENCORE FORM — RSVP card styled as the closing track label
 *         on the B-side of a record.
 */
export function SonataLayout({ formSlot, program, title, dateTime, location, description, galleryImages }: JoinLayoutProps) {
  const lenisRef = useRef<Lenis | null>(null);
  const tiltRef = useRef<HTMLDivElement>(null);

  const d = dateTime ? new Date(dateTime) : null;
  const day = d?.getDate().toString().padStart(2, "0") ?? "--";
  const monthShort = d?.toLocaleString("en-US", { month: "short" }).toUpperCase() ?? "—";
  const year = d?.getFullYear() ?? 2026;
  const time = d?.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false }) ?? "--:--";

  // Tracks structure
  const tracks = [
    { num: "01", title: "Prelude",   runtime: "0:48", side: "A", desc: "An overture for the heart" },
    { num: "02", title: "Encounter", runtime: "4:12", side: "A", desc: "A spring evening, no end in sight" },
    { num: "03", title: "Voyages",   runtime: "5:36", side: "A", desc: "Three cities, infinite memory" },
    { num: "04", title: "The Vow",   runtime: "3:24", side: "B", desc: "By the sea, beneath the stars" },
    { num: "05", title: "Ceremony",  runtime: "6:08", side: "B", desc: `${day} ${monthShort} ${year}` },
    { num: "06", title: "Encore",    runtime: "—:—", side: "B", desc: "Reply requested" },
  ];

  useEffect(() => {
    const card = tiltRef.current;
    if (!card) return;
    const handleMove = (e: MouseEvent) => {
      const r = card.getBoundingClientRect();
      const dx = (e.clientX - (r.left + r.width / 2)) / r.width;
      const dy = (e.clientY - (r.top + r.height / 2)) / r.height;
      card.style.transform = `perspective(1200px) rotateY(${dx * 8}deg) rotateX(${-dy * 8}deg)`;
    };
    const reset = () => { card.style.transform = "perspective(1200px) rotateY(0) rotateX(0)"; };
    window.addEventListener("mousemove", handleMove);
    card.addEventListener("mouseleave", reset);
    return () => {
      window.removeEventListener("mousemove", handleMove);
      card.removeEventListener("mouseleave", reset);
    };
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    const lenis = createLenis({ duration: 1.6, easing: (t) => 1 - Math.pow(1 - t, 5) });
    lenis.scrollTo(0, { immediate: true });
    lenisRef.current = lenis;
    const ticker = (time: number) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);
    lenis.on("scroll", ScrollTrigger.update);

    const ctx = gsap.context(() => {
      // ── ACT I: Cover entrance ───────────────────────────────────────
      const tl = gsap.timeline({ defaults: { ease: "expo.out" } });
      tl.from(".sn-marquee-t", { x: "100%", duration: 1.6 }, 0);
      tl.from(".sn-marquee-b", { x: "-100%", duration: 1.6 }, 0);
      tl.from(".sn-cover-side", { x: -60, opacity: 0, duration: 1.4 }, 0.3);
      tl.from(".sn-vinyl-stage", { x: 60, opacity: 0, scale: 0.85, duration: 1.4 }, 0.4);

      const coverSplit = new SplitText(".sn-cover-title", { type: "chars" });
      tl.from(coverSplit.chars, { y: 40, opacity: 0, stagger: 0.04, duration: 1.1 }, 0.6);

      tl.from(".sn-track-row", { x: -30, opacity: 0, stagger: 0.08, duration: 0.7 }, 0.8);

      // ── ACT II: PINNED VINYL + TONE-ARM ─────────────────────────────
      const pinned = document.querySelector<HTMLElement>(".sn-pin-section");
      const toneArm = document.querySelector<HTMLElement>(".sn-tone-arm");
      const trackLabels = document.querySelectorAll<HTMLElement>(".sn-track-label");
      const albumImgs = document.querySelectorAll<HTMLElement>(".sn-album-bg");
      const waveLines = document.querySelectorAll<SVGElement>(".sn-wave-line");

      if (pinned && toneArm) {
        // Tone-arm rotation: -8° at start (lifted) → 0° → 8° → 16° → 24° → 32° → 40° (across)
        ScrollTrigger.create({
          trigger: pinned,
          start: "top top",
          end: "+=500%",
          pin: true,
          scrub: 1,
          onUpdate(self) {
            const stage = Math.min(5, Math.floor(self.progress * 6));
            const angle = -8 + (self.progress * 48);
            if (toneArm) toneArm.style.transform = `rotate(${angle}deg)`;

            trackLabels.forEach((label, i) => {
              label.style.opacity = i === stage ? "1" : "0";
            });
            albumImgs.forEach((img, i) => {
              img.style.opacity = i === stage ? "0.4" : "0";
            });
            // Animate wavelines based on stage
            waveLines.forEach((w, i) => {
              const active = i === stage;
              w.style.opacity = active ? "1" : "0.2";
            });
          },
        });
      }

      // ── ACT III: AUDIO WAVEFORM SCROLL ──────────────────────────────
      gsap.from(".sn-wf-bar", {
        scaleY: 0,
        transformOrigin: "center bottom",
        stagger: { each: 0.012, from: "center" },
        duration: 0.8,
        ease: "power3.out",
        scrollTrigger: { trigger: ".sn-wf-section", start: "top 80%", toggleActions: "play reverse play reverse" },
      });

      // Playhead moves across waveform as you scroll within section
      gsap.fromTo(".sn-playhead", { x: "0%" }, {
        x: "100%",
        ease: "none",
        scrollTrigger: { trigger: ".sn-wf-section", start: "top top", end: "bottom top", scrub: 1 },
      });

      gsap.from(".sn-wf-label", { y: 20, opacity: 0, stagger: 0.1, duration: 1, ease: "power3.out",
        scrollTrigger: { trigger: ".sn-wf-section", start: "top 75%", toggleActions: "play reverse play reverse" } });

      // ── ACT IV: SIDE A/B GALLERY ────────────────────────────────────
      document.querySelectorAll(".sn-sleeve").forEach((sleeve, i) => {
        gsap.from(sleeve, {
          y: 60,
          opacity: 0,
          rotateZ: i % 2 === 0 ? -3 : 3,
          duration: 1.2,
          delay: i * 0.08,
          ease: "expo.out",
          scrollTrigger: { trigger: ".sn-sides-section", start: "top 75%", toggleActions: "play reverse play reverse" },
        });
      });
      gsap.from(".sn-side-divider", { scaleY: 0, transformOrigin: "top", duration: 1.6, ease: "expo.out",
        scrollTrigger: { trigger: ".sn-sides-section", start: "top 75%", toggleActions: "play reverse play reverse" } });
      gsap.from(".sn-side-title", { y: 30, opacity: 0, stagger: 0.2, duration: 1, ease: "expo.out",
        scrollTrigger: { trigger: ".sn-sides-section", start: "top 75%", toggleActions: "play reverse play reverse" } });

      // ── ACT V: METADATA TABLE ───────────────────────────────────────
      const dayEl = document.querySelector<HTMLElement>(".sn-num-day");
      const monthEl = document.querySelector<HTMLElement>(".sn-num-month");
      if (dayEl && monthEl && d) {
        const obj = { d: 0, m: 0 };
        gsap.to(obj, {
          d: d.getDate(), m: d.getMonth() + 1,
          duration: 1.4, ease: "power3.out",
          scrollTrigger: { trigger: ".sn-meta-section", start: "top 70%", toggleActions: "play reverse play reverse" },
          onUpdate() {
            dayEl.textContent = Math.round(obj.d).toString().padStart(2, "0");
            monthEl.textContent = Math.round(obj.m).toString().padStart(2, "0");
          },
        });
      }
      gsap.from(".sn-meta-row", { x: -40, opacity: 0, stagger: 0.1, duration: 0.9, ease: "expo.out",
        scrollTrigger: { trigger: ".sn-meta-section", start: "top 70%", toggleActions: "play reverse play reverse" } });
      gsap.from(".sn-meta-rule", { scaleX: 0, transformOrigin: "left", duration: 1.4, ease: "expo.inOut",
        scrollTrigger: { trigger: ".sn-meta-section", start: "top 70%", toggleActions: "play reverse play reverse" } });

      // Form
      gsap.from(".sn-form-card", { opacity: 0, y: 60, duration: 1.4, ease: "expo.out",
        scrollTrigger: { trigger: ".sn-form-card", start: "top 80%", toggleActions: "play reverse play reverse" } });
    });

    return () => {
      lenis.destroy();
      gsap.ticker.remove(ticker);
      ScrollTrigger.killAll();
      ctx.revert();
    };
  }, []);

  // Vinyl record SVG component
  const Vinyl = ({ size = 600, label = "A SIDE", labelImg }: { size?: number; label?: string; labelImg?: string }) => (
    <svg viewBox={`0 0 ${size} ${size}`} className="sn-vinyl-disk" style={{ width: "100%", height: "100%" }}>
      <defs>
        <radialGradient id={`sn-vinyl-${size}`}>
          <stop offset="0%" stopColor="#1c1c1c" />
          <stop offset="40%" stopColor="#0a0a0a" />
          <stop offset="100%" stopColor="#000" />
        </radialGradient>
        <radialGradient id={`sn-label-${size}`}>
          <stop offset="0%" stopColor="#c9a04e" />
          <stop offset="100%" stopColor="#8b6914" />
        </radialGradient>
        {labelImg && (
          <pattern id={`sn-labelimg-${size}`} x="0" y="0" width="1" height="1" patternUnits="objectBoundingBox">
            <image href={labelImg} x="0" y="0" width={size * 0.3} height={size * 0.3} preserveAspectRatio="xMidYMid slice" />
          </pattern>
        )}
      </defs>

      {/* Vinyl disc */}
      <circle cx={size / 2} cy={size / 2} r={size * 0.48} fill={`url(#sn-vinyl-${size})`} />

      {/* Concentric grooves */}
      {Array.from({ length: 22 }).map((_, i) => (
        <circle
          key={i}
          cx={size / 2}
          cy={size / 2}
          r={size * 0.2 + (i / 22) * (size * 0.27)}
          fill="none"
          stroke="rgba(255,255,255,0.04)"
          strokeWidth="1"
        />
      ))}

      {/* Reflective highlight (off-center light hint) */}
      <ellipse cx={size * 0.35} cy={size * 0.3} rx={size * 0.18} ry={size * 0.08} fill="rgba(255,255,255,0.04)" />

      {/* Label */}
      <circle cx={size / 2} cy={size / 2} r={size * 0.18} fill={labelImg ? `url(#sn-labelimg-${size})` : `url(#sn-label-${size})`} stroke="#5a3f0e" strokeWidth="1" />

      {/* Center hole */}
      <circle cx={size / 2} cy={size / 2} r={size * 0.012} fill="#000" />

      {/* Label text */}
      {!labelImg && (
        <>
          <text x={size / 2} y={size / 2 - size * 0.05} textAnchor="middle" style={{ fontFamily: "'Playfair Display', serif", fontSize: size * 0.04, fill: "#0d0d0d", fontWeight: 700 }}>
            {label}
          </text>
          <text x={size / 2} y={size / 2 + size * 0.02} textAnchor="middle" style={{ fontFamily: "'Playfair Display', serif", fontSize: size * 0.022, fill: "#3a2c10", fontStyle: "italic" }}>
            33⅓ RPM
          </text>
          <text x={size / 2} y={size / 2 + size * 0.06} textAnchor="middle" style={{ fontFamily: "monospace", fontSize: size * 0.016, fill: "#3a2c10", letterSpacing: 2 }}>
            {year}
          </text>
        </>
      )}
    </svg>
  );

  return (
    <main className="relative overflow-x-hidden" style={{ background: "#1a0608", color: "#f4e9d8", fontFamily: "'Inter', system-ui, sans-serif" }}>
      <style>{`
        @keyframes snSpin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
        @keyframes snMQ_L { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes snMQ_R { from { transform: translateX(-50%); } to { transform: translateX(0); } }
        .sn-spin { animation: snSpin 18s linear infinite; }
        .sn-spin-fast { animation: snSpin 4s linear infinite; }
      `}</style>

      {/* Background gradient — burgundy with grain */}
      <div className="fixed inset-0 -z-30 pointer-events-none" style={{
        background: "radial-gradient(ellipse at top, #3a0c12 0%, #1a0608 60%), #0e0306",
      }} />

      {/* Subtle grain */}
      <svg className="fixed inset-0 w-full h-full pointer-events-none z-[1] opacity-[0.05] mix-blend-overlay">
        <filter id="sn-grain">
          <feTurbulence type="fractalNoise" baseFrequency="1.4" numOctaves="2" stitchTiles="stitch" />
          <feColorMatrix values="0.3 0 0 0 0  0.2 0 0 0 0  0.1 0 0 0 0  0 0 0 1 0" />
        </filter>
        <rect width="100%" height="100%" filter="url(#sn-grain)" />
      </svg>

      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-30 px-8 py-4 flex justify-between items-center text-[10px] uppercase tracking-[0.5em]"
        style={{ background: "rgba(26, 6, 8, 0.7)", backdropFilter: "blur(10px)", borderBottom: "1px solid rgba(201, 160, 78, 0.25)" }}>
        <span style={{ color: "#c9a04e" }}>Sonata · LP {year}</span>
        <span className="text-[#f4e9d8]/60">▶ NOW PLAYING</span>
        <span style={{ color: "#c9a04e" }}>33⅓ RPM</span>
      </header>

      {/* ═══ ACT I: ALBUM COVER ════════════════════════════════════════ */}
      <section className="relative min-h-screen flex flex-col justify-between overflow-hidden">
        {/* Top marquee */}
        <div className="sn-marquee-t relative h-10 border-b border-[#c9a04e]/20 overflow-hidden flex items-center mt-12">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase" style={{ color: "#c9a04e", animation: "snMQ_L 35s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">
                {title.toUpperCase()} ▸ A LIVE RECORDING ▸ {monthShort} {day} {year} ▸ 6 TRACKS ▸ MASTERED IN {(location ?? "").split(",")[0].toUpperCase()} ▸
              </span>
            ))}
          </div>
        </div>

        {/* Album cover layout */}
        <div className="flex-1 flex items-center justify-center px-6 sm:px-12 py-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center max-w-6xl w-full">
            {/* Left: tracklist & info */}
            <div className="sn-cover-side">
              <p className="text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#c9a04e" }}>— An Album In Six Tracks —</p>
              <h1 className="sn-cover-title font-light leading-[0.85] tracking-[-0.03em] mb-6" style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: "clamp(3rem, 9vw, 7rem)",
              }}>
                {title}
              </h1>

              <div className="flex items-center gap-3 mb-8 text-[10px] uppercase tracking-[0.4em] text-[#f4e9d8]/60">
                <span>VOL. I</span>
                <span style={{ color: "#c9a04e" }}>◆</span>
                <span>{monthShort} {day}</span>
                <span style={{ color: "#c9a04e" }}>◆</span>
                <span>{year}</span>
              </div>

              {/* Tracklist */}
              <div className="space-y-1">
                <div className="flex items-baseline justify-between border-b border-[#c9a04e]/20 pb-1 mb-2">
                  <span className="text-[10px] uppercase tracking-[0.5em]" style={{ color: "#c9a04e" }}>Side A</span>
                  <span className="text-[10px] tracking-[0.3em] text-[#f4e9d8]/40">10:36</span>
                </div>
                {tracks.filter(t => t.side === "A").map((t, i) => (
                  <div key={i} className="sn-track-row flex items-baseline justify-between py-2 group hover:translate-x-2 transition-transform">
                    <div className="flex items-baseline gap-4">
                      <span className="text-[10px] tracking-[0.3em]" style={{ color: "#c9a04e" }}>{t.num}</span>
                      <span className="italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>{t.title}</span>
                    </div>
                    <span className="text-xs tabular-nums tracking-wide text-[#f4e9d8]/50">{t.runtime}</span>
                  </div>
                ))}

                <div className="flex items-baseline justify-between border-b border-[#c9a04e]/20 pb-1 mb-2 mt-6">
                  <span className="text-[10px] uppercase tracking-[0.5em]" style={{ color: "#c9a04e" }}>Side B</span>
                  <span className="text-[10px] tracking-[0.3em] text-[#f4e9d8]/40">9:32+</span>
                </div>
                {tracks.filter(t => t.side === "B").map((t, i) => (
                  <div key={i} className="sn-track-row flex items-baseline justify-between py-2 group hover:translate-x-2 transition-transform">
                    <div className="flex items-baseline gap-4">
                      <span className="text-[10px] tracking-[0.3em]" style={{ color: "#c9a04e" }}>{t.num}</span>
                      <span className="italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>{t.title}</span>
                    </div>
                    <span className="text-xs tabular-nums tracking-wide text-[#f4e9d8]/50">{t.runtime}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: rotating vinyl */}
            <div className="sn-vinyl-stage relative aspect-square w-full max-w-md mx-auto">
              <div className="sn-spin absolute inset-0">
                <Vinyl size={600} label={title.split("&")[0]?.trim() ?? "A SIDE"} />
              </div>
              {/* Tone arm visual hint */}
              <div className="absolute -top-8 -right-8 w-32 h-32 pointer-events-none" style={{ transformOrigin: "100% 0%", transform: "rotate(-15deg)" }}>
                <svg viewBox="0 0 120 120">
                  <line x1="110" y1="10" x2="40" y2="80" stroke="#c9a04e" strokeWidth="3" strokeLinecap="round" />
                  <circle cx="110" cy="10" r="8" fill="#1a0608" stroke="#c9a04e" strokeWidth="2" />
                  <circle cx="40" cy="80" r="6" fill="#c9a04e" />
                </svg>
              </div>
            </div>
          </div>
        </div>

        <div className="sn-marquee-b relative h-10 border-t border-[#c9a04e]/20 overflow-hidden flex items-center">
          <div className="whitespace-nowrap text-[10px] tracking-[0.5em] uppercase text-[#f4e9d8]/60" style={{ animation: "snMQ_R 40s linear infinite" }}>
            {Array(10).fill(null).map((_, i) => (
              <span key={i} className="mx-6">◇ PRODUCED BY LIFE ◇ ENGINEERED BY LOVE ◇ {(location ?? "").toUpperCase()} ◇</span>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ ACT II: PINNED VINYL + TONE-ARM ═══════════════════════════ */}
      <section className="sn-pin-section relative h-screen overflow-hidden">
        <div className="absolute top-12 left-12 text-[10px] uppercase tracking-[0.5em] z-30" style={{ color: "#c9a04e" }}>II · TURNTABLE</div>
        <div className="absolute top-12 right-12 text-[10px] uppercase tracking-[0.5em] text-[#f4e9d8]/40 z-30">▶ SCROLL TO PLAY</div>

        {/* Album image backgrounds (cross-fade per track) */}
        {tracks.map((_, i) => (
          <div
            key={i}
            className={`sn-album-bg absolute inset-0`}
            style={{ opacity: i === 0 ? 0.4 : 0, transition: "opacity 0.4s" }}
          >
            <img src={galleryImages[i % galleryImages.length]} alt="" className="w-full h-full object-cover" style={{ filter: "sepia(0.4) saturate(1.2) brightness(0.4)" }} />
          </div>
        ))}

        {/* Center stage */}
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="relative w-[min(70vw,32rem)] h-[min(70vw,32rem)]">
            {/* Spinning vinyl */}
            <div className="sn-spin absolute inset-0">
              <Vinyl size={600} label={title.split("&")[1]?.trim() ?? "B SIDE"} labelImg={galleryImages[2]} />
            </div>

            {/* Tone-arm (anchored top-right, rotates from pivot) */}
            <div className="absolute" style={{ top: "-12%", right: "-12%", width: "70%", height: "70%", transformOrigin: "100% 0%" }}>
              <div ref={(el) => { if (el) el.classList.add("sn-tone-arm"); }} style={{ transform: "rotate(-8deg)", transformOrigin: "100% 0%", width: "100%", height: "100%", transition: "transform 0.05s linear" }}>
                <svg viewBox="0 0 400 400" className="w-full h-full">
                  {/* Arm */}
                  <line x1="380" y1="20" x2="180" y2="280" stroke="#c9a04e" strokeWidth="6" strokeLinecap="round" />
                  {/* Pivot */}
                  <circle cx="380" cy="20" r="22" fill="#1a0608" stroke="#c9a04e" strokeWidth="4" />
                  <circle cx="380" cy="20" r="12" fill="#c9a04e" />
                  {/* Cartridge head */}
                  <rect x="155" y="265" width="50" height="30" rx="2" fill="#c9a04e" transform="rotate(50 180 280)" />
                  {/* Needle */}
                  <line x1="180" y1="280" x2="195" y2="305" stroke="#0a0a0a" strokeWidth="2" />
                </svg>
              </div>
            </div>

            {/* Current track label */}
            <div className="absolute -bottom-16 left-1/2 -translate-x-1/2 w-full text-center">
              {tracks.map((t, i) => (
                <div key={i} className="sn-track-label absolute inset-0" style={{ opacity: i === 0 ? 1 : 0, transition: "opacity 0.3s" }}>
                  <p className="text-[10px] uppercase tracking-[0.5em] mb-1" style={{ color: "#c9a04e" }}>TRACK {t.num} · {t.runtime}</p>
                  <p className="italic font-light" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(1.5rem, 3.5vw, 2.5rem)" }}>{t.title}</p>
                  <p className="text-sm italic text-[#f4e9d8]/60 mt-1" style={{ fontFamily: "'Playfair Display', serif" }}>{t.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Mini waveform indicator at bottom */}
        <div className="absolute bottom-6 left-12 right-12 flex items-end gap-1 h-8 pointer-events-none">
          {Array.from({ length: 6 }).map((_, i) => {
            const heights = ["40%", "70%", "55%", "85%", "60%", "30%"];
            return (
              <div key={i} className="sn-wave-line flex-1 transition-opacity duration-300" style={{ opacity: i === 0 ? 1 : 0.2 }}>
                <div style={{ background: "#c9a04e", height: heights[i], width: "100%" }} />
              </div>
            );
          })}
        </div>
      </section>

      {/* ═══ ACT III: AUDIO WAVEFORM ═══════════════════════════════════ */}
      <section className="sn-wf-section relative py-32 px-6 overflow-hidden">
        <div className="max-w-6xl mx-auto">
          <p className="sn-wf-label text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#c9a04e" }}>III · THE RECORDING</p>
          <h2 className="sn-wf-label text-center font-light italic leading-tight mb-20" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2.5rem, 5vw, 4.5rem)" }}>
            Listen to the waveform.
          </h2>

          {/* Waveform — 80 bars with varying heights */}
          <div className="relative flex items-center justify-center gap-1 h-48 mb-12">
            {Array.from({ length: 80 }).map((_, i) => {
              // Pseudorandom but deterministic height
              const h = Math.abs(Math.sin(i * 0.37) * Math.cos(i * 0.13)) * 80 + 10;
              const color = i % 13 === 0 ? "#c9a04e" : "#f4e9d8";
              return (
                <div
                  key={i}
                  className="sn-wf-bar flex-1 rounded-sm"
                  style={{
                    height: `${h}%`,
                    background: color,
                    opacity: i % 13 === 0 ? 1 : 0.45,
                    maxWidth: 6,
                  }}
                />
              );
            })}

            {/* Playhead — vertical bar that scrubs */}
            <div className="sn-playhead absolute top-0 bottom-0 w-px pointer-events-none" style={{ background: "#c9a04e", left: 0, boxShadow: "0 0 12px #c9a04e" }}>
              <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-2 h-2 rotate-45" style={{ background: "#c9a04e" }} />
            </div>
          </div>

          {/* Timecode markers */}
          <div className="flex justify-between text-[10px] tabular-nums tracking-widest text-[#f4e9d8]/40">
            <span>00:00</span>
            <span>05:18</span>
            <span>10:36</span>
            <span>15:54</span>
            <span>21:00</span>
          </div>

          <p className="sn-wf-label text-center italic text-lg mt-12 text-[#f4e9d8]/60" style={{ fontFamily: "'Playfair Display', serif" }}>
            ~ 21 minutes of becoming ~
          </p>
        </div>
      </section>

      {/* ═══ ACT IV: SIDE A / SIDE B SLEEVES ═══════════════════════════ */}
      <section className="sn-sides-section relative py-32 px-6">
        <div className="max-w-6xl mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#c9a04e" }}>IV · THE SLEEVES</p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-16 relative">
            {/* Divider */}
            <div className="sn-side-divider hidden md:block absolute left-1/2 top-0 bottom-0 w-px" style={{ background: "linear-gradient(180deg, transparent, #c9a04e 20%, #c9a04e 80%, transparent)" }} />

            {/* Side A */}
            <div>
              <div className="flex items-baseline justify-between mb-10">
                <h3 className="sn-side-title font-light italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                  Side A
                </h3>
                <p className="sn-side-title text-[10px] uppercase tracking-[0.4em]" style={{ color: "#c9a04e" }}>Tracks 1–3</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {tracks.filter(t => t.side === "A").map((t, i) => (
                  <div key={i} className={`sn-sleeve relative aspect-square ${i === 0 ? "col-span-2" : ""}`} style={{
                    background: `url(${galleryImages[i]}) center/cover`,
                    boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
                    border: "1px solid rgba(201, 160, 78, 0.2)",
                  }}>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.4em]" style={{ color: "#c9a04e" }}>0{i + 1}</div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>{t.title}</p>
                      <p className="text-[10px] tabular-nums tracking-[0.4em] mt-1 text-[#f4e9d8]/70">{t.runtime}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Side B */}
            <div>
              <div className="flex items-baseline justify-between mb-10">
                <h3 className="sn-side-title font-light italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
                  Side B
                </h3>
                <p className="sn-side-title text-[10px] uppercase tracking-[0.4em]" style={{ color: "#c9a04e" }}>Tracks 4–6</p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {tracks.filter(t => t.side === "B").map((t, i) => (
                  <div key={i} className={`sn-sleeve relative aspect-square ${i === 1 ? "col-span-2" : ""}`} style={{
                    background: `url(${galleryImages[i + 3]}) center/cover`,
                    boxShadow: "0 16px 40px rgba(0,0,0,0.5)",
                    border: "1px solid rgba(201, 160, 78, 0.2)",
                  }}>
                    <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent" />
                    <div className="absolute top-3 left-3 text-[10px] uppercase tracking-[0.4em]" style={{ color: "#c9a04e" }}>0{i + 4}</div>
                    <div className="absolute bottom-4 left-4 right-4">
                      <p className="italic" style={{ fontFamily: "'Playfair Display', serif", fontSize: 22 }}>{t.title}</p>
                      <p className="text-[10px] tabular-nums tracking-[0.4em] mt-1 text-[#f4e9d8]/70">{t.runtime}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══ ACT V: METADATA TABLE ═════════════════════════════════════ */}
      <section className="sn-meta-section relative py-32 px-6">
        <div className="max-w-3xl mx-auto">
          <p className="text-center text-[10px] uppercase tracking-[0.6em] mb-3" style={{ color: "#c9a04e" }}>V · LINER NOTES</p>
          <h2 className="text-center font-light italic leading-tight mb-16" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3.5rem)" }}>
            Album Metadata
          </h2>

          {/* Big release date */}
          <div className="flex items-center justify-center gap-6 mb-16">
            <p className="sn-num-day font-light tabular-nums leading-none tracking-[-0.05em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5rem, 12vw, 10rem)" }}>00</p>
            <div className="font-light leading-none" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(3rem, 8vw, 7rem)", color: "#c9a04e" }}>·</div>
            <p className="sn-num-month font-light tabular-nums leading-none tracking-[-0.05em]" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(5rem, 12vw, 10rem)" }}>00</p>
          </div>

          {/* Metadata rows */}
          <div className="space-y-3 font-mono text-sm">
            {[
              { k: "Release Date", v: d?.toLocaleDateString("en-US", { day: "numeric", month: "long", year: "numeric" }) ?? "—" },
              { k: "Runtime", v: "Approx. 21:00 + Encore" },
              { k: "Time", v: time },
              { k: "Catalog №", v: `SON-${year}-001` },
              { k: "Mastered At", v: location?.split(",")[0] ?? "—" },
              { k: "Format", v: "180g Heavyweight Vinyl · A/B Sides" },
              { k: "Conducted By", v: title.replace("&", "×") },
            ].map((row, i) => (
              <div key={i} className="sn-meta-row flex justify-between items-baseline border-b border-[#c9a04e]/15 pb-2">
                <span className="text-[10px] uppercase tracking-[0.3em] text-[#f4e9d8]/40">{row.k}</span>
                <span className="text-base" style={{ color: "#c9a04e" }}>{row.v}</span>
              </div>
            ))}
            <div className="sn-meta-rule h-px mt-4" style={{ background: "linear-gradient(90deg, transparent, #c9a04e, transparent)" }} />
          </div>
        </div>
      </section>

      {/* ═══ ACT VI: ENCORE FORM ═══════════════════════════════════════ */}
      <section className="relative min-h-screen flex items-center justify-center px-6 py-32" style={{ perspective: 1200 }}>
        <div
          ref={tiltRef}
          className="sn-form-card w-full max-w-md p-12 relative transition-transform duration-300 ease-out text-center"
          style={{
            background: "linear-gradient(135deg, #3a0c12 0%, #1a0608 100%)",
            border: "1px solid rgba(201, 160, 78, 0.3)",
            boxShadow: "inset 0 1px 0 rgba(201, 160, 78, 0.2), 0 40px 100px rgba(0, 0, 0, 0.7)",
            transformStyle: "preserve-3d",
          }}
        >
          {/* Vinyl record decoration on top (smaller) */}
          <div className="sn-spin absolute -top-12 left-1/2 -translate-x-1/2 w-24 h-24 pointer-events-none">
            <Vinyl size={200} label="B6" />
          </div>

          <div className="absolute top-3 left-3 w-5 h-5 border-t border-l" style={{ borderColor: "#c9a04e" }} />
          <div className="absolute top-3 right-3 w-5 h-5 border-t border-r" style={{ borderColor: "#c9a04e" }} />
          <div className="absolute bottom-3 left-3 w-5 h-5 border-b border-l" style={{ borderColor: "#c9a04e" }} />
          <div className="absolute bottom-3 right-3 w-5 h-5 border-b border-r" style={{ borderColor: "#c9a04e" }} />

          <p className="text-[10px] uppercase tracking-[0.6em] mb-3 mt-16" style={{ color: "#c9a04e" }}>VI · ENCORE</p>
          <h3 className="font-light italic mb-3" style={{ fontFamily: "'Playfair Display', serif", fontSize: "clamp(2rem, 4vw, 3rem)" }}>
            Request a seat
          </h3>
          <p className="italic mb-10 text-[#c9a04e]/80" style={{ fontFamily: "'Playfair Display', serif", fontSize: 16 }}>
            ~ Side B · Track 06 ~
          </p>
          <div className="mx-auto w-12 h-px mb-10" style={{ background: "#c9a04e" }} />

          <WeddingProgram entries={program} className="mb-10" />
          {formSlot}
        </div>
      </section>
    </main>
  );
}
